import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notify } from "../../utils/notify";
import CollectionTable from "./CollectionTable";
import { EntityWithCount, SortConfig, ColumnConfig } from "../../types/cms";
import { useEntitiesWithCount } from "../../hooks/useCollectionData";
import { deleteEntity, EntityKind } from "../../lib/videoAdmin";
import { firstLogo } from "../../lib/catalog";
import { EntityCreateModal } from "../admin/EntityPicker";
import { inputClass, primaryButton } from "../admin/ui";

interface Props {
  kind: EntityKind;
  onShowVideos: (entity: { id: string; name: string }) => void;
}

export default function EntitiesCollectionView({ kind, onShowVideos }: Props) {
  const navigate = useNavigate();
  const { entities, loading, refetch } = useEntitiesWithCount(kind);
  const isBrand = kind === "companies";

  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({ field: "name", direction: "asc" });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);

  const rows = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = entities.filter(
      (e) => !query || e.name?.toLowerCase().includes(query) || e.description?.toLowerCase().includes(query),
    );
    if (!sortConfig) return filtered;
    return [...filtered].sort((a, b) => {
      const av = sortConfig.field === "videoCount" ? a.videoCount : a.name.toLowerCase();
      const bv = sortConfig.field === "videoCount" ? b.videoCount : b.name.toLowerCase();
      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [entities, searchQuery, sortConfig]);

  const handleDelete = async (entity: EntityWithCount) => {
    const warning = entity.videoCount
      ? `« ${entity.name} » est liée à ${entity.videoCount} vidéo(s), qui s'afficheront sans ${isBrand ? "marque" : "studio"}. Supprimer quand même ?`
      : `Supprimer « ${entity.name} » ?`;
    if (!window.confirm(warning)) return;
    try {
      await deleteEntity(kind, entity.id);
      notify({ isSuccess: true, message: `${isBrand ? "Marque supprimée" : "Studio supprimé"}` });
      refetch();
    } catch (error) {
      notify({
        isSuccess: false,
        message: "Suppression impossible",
        info: error instanceof Error ? error.message : "Réessayez dans un instant.",
      });
    }
  };

  const columns: ColumnConfig<EntityWithCount>[] = [
    { key: "logo", label: "Logo", width: "w-20" },
    { key: "name", label: "Nom", sortable: true },
    { key: "description", label: "Description" },
    { key: "website", label: "Site web", width: "w-48" },
    { key: "videoCount", label: "Vidéos", sortable: true, width: "w-24" },
  ];

  const renderRow = (entity: EntityWithCount) => {
    const isSelected = selectedIds.has(entity.id);
    return (
      <tr key={entity.id} className={`hover:bg-neutral-50 transition-colors ${isSelected ? "bg-neutral-100" : ""}`}>
        <td className="px-3 py-3">
          <input
            type="checkbox"
            checked={isSelected}
            aria-label={`Sélectionner ${entity.name}`}
            onChange={() =>
              setSelectedIds((prev) => {
                const next = new Set(prev);
                next.has(entity.id) ? next.delete(entity.id) : next.add(entity.id);
                return next;
              })
            }
            className="w-4 h-4 rounded border-gray-300 text-neutral-900 focus:ring-neutral-900 cursor-pointer"
          />
        </td>
        <td className="px-4 py-3">
          {firstLogo(entity) ? (
            <img src={firstLogo(entity)} alt="" className="w-10 h-10 object-contain rounded border border-gray-200 bg-white" />
          ) : (
            <div className="w-10 h-10 bg-gray-100 rounded border border-gray-200" />
          )}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900">{entity.name}</td>
        <td className="px-4 py-3">
          <div className="text-sm text-gray-600 truncate max-w-xs">{entity.description || "—"}</div>
        </td>
        <td className="px-4 py-3">
          {entity.websiteURL ? (
            <a href={entity.websiteURL} target="_blank" rel="noopener noreferrer" className="text-sm text-neutral-700 underline truncate block max-w-[12rem]">
              {entity.websiteURL.replace(/^https?:\/\/(www\.)?/, "")}
            </a>
          ) : (
            <span className="text-sm text-gray-400">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          <button
            onClick={() => onShowVideos({ id: entity.id, name: entity.name })}
            className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium tabular-nums bg-neutral-100 hover:bg-neutral-200"
            title="Voir les vidéos"
          >
            {entity.videoCount}
          </button>
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-3">
            <button onClick={() => navigate(`/admin/${isBrand ? "brand" : "studio"}/${entity.id}`)} className="text-sm underline text-neutral-700 hover:text-neutral-950">
              Modifier
            </button>
            <button onClick={() => handleDelete(entity)} className="text-sm underline text-red-700 hover:text-red-900">
              Supprimer
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          placeholder="Rechercher par nom ou description…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`${inputClass} flex-1 min-w-[220px]`}
        />
        <span className="text-sm text-gray-600 tabular-nums">
          {rows.length} {isBrand ? `marque${rows.length !== 1 ? "s" : ""}` : `studio${rows.length !== 1 ? "s" : ""}`}
        </span>
        <button className={primaryButton} onClick={() => setCreating(true)}>
          {isBrand ? "Nouvelle marque" : "Nouveau studio"}
        </button>
      </div>

      <CollectionTable
        data={rows}
        columns={columns}
        selectedIds={selectedIds}
        onSelectAll={() =>
          setSelectedIds((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))))
        }
        onSelectItem={() => undefined}
        sortConfig={sortConfig}
        onSort={(field) =>
          setSortConfig((current) =>
            current?.field === field
              ? { field, direction: current.direction === "asc" ? "desc" : "asc" }
              : { field, direction: "asc" },
          )
        }
        renderRow={renderRow}
        loading={loading}
      />

      <EntityCreateModal
        open={creating}
        kind={kind}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          refetch();
        }}
      />
    </div>
  );
}
